import React, { useState, useMemo } from 'react'
import { Search, Filter, Shield, DollarSign } from 'lucide-react'
import { SecretMenuItem } from '../../lib/supabase'
import { CalculatedEventStatus } from '../../lib/utils/eventStatus'
import MenuItemCard from './MenuItemCard'

interface MenuGridProps {
  items: SecretMenuItem[]
  loading?: boolean
  className?: string
  eventStatus?: CalculatedEventStatus
}

type SortOption = 'name' | 'price_low' | 'price_high' | 'exclusive_first'
type FilterOption = 'all' | 'exclusive' | 'available'

const MenuGrid: React.FC<MenuGridProps> = ({ items, loading = false, className = '', eventStatus }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('exclusive_first')
  const [filterBy, setFilterBy] = useState<FilterOption>('all')
  const [showFilters, setShowFilters] = useState(false)

  // Filter and sort items
  const filteredAndSortedItems = useMemo(() => {
    let filtered = items.filter(item => {
      // Search filter
      const matchesSearch = !searchQuery || 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
      
      // Category filter
      let matchesFilter = true
      switch (filterBy) {
        case 'exclusive':
          matchesFilter = item.is_secret_exclusive
          break
        case 'available':
          matchesFilter = item.is_available && item.current_quantity < item.max_quantity
          break
        default:
          matchesFilter = true
      }

      return matchesSearch && matchesFilter
    })

    // Sort items
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'price_low':
          return a.price - b.price
        case 'price_high':
          return b.price - a.price
        case 'exclusive_first':
          // SECRET items first, then by name
          if (a.is_secret_exclusive && !b.is_secret_exclusive) return -1
          if (!a.is_secret_exclusive && b.is_secret_exclusive) return 1
          return a.name.localeCompare(b.name)
        default:
          return 0
      }
    })

    return filtered
  }, [items, searchQuery, sortBy, filterBy])

  const exclusiveCount = items.filter(item => item.is_secret_exclusive).length
  const availableCount = items.filter(item => item.is_available && item.current_quantity < item.max_quantity).length

  if (loading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-secret-bg-secondary rounded-lg overflow-hidden">
              <div className="h-48 bg-secret-bg-tertiary animate-pulse" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-secret-bg-tertiary rounded animate-pulse" />
                <div className="h-3 bg-secret-bg-tertiary rounded animate-pulse w-3/4" />
                <div className="h-10 bg-secret-bg-tertiary rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 dark:hidden"
            size={20}
            color="#6b7280"
          />
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 hidden dark:block text-muted-foreground"
            size={20}
          />
          <input
            type="text"
            placeholder="Search menu items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-lg text-foreground placeholder:text-gray-500 dark:placeholder:text-muted-foreground focus:border-secret-primary focus:outline-none transition-colors"
          />
        </div>

        {/* Filter Toggle */}
        <div className="flex justify-between items-center">
          <div className="text-muted-foreground">
            {filteredAndSortedItems.length} {filteredAndSortedItems.length === 1 ? 'item' : 'items'}
            {searchQuery && ` for "${searchQuery}"`}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Filter size={16} />
            <span>Filters</span>
          </button>
        </div>

        {/* Filter Controls */}
        {showFilters && (
          <div className="bg-card rounded-lg p-4 space-y-4 border border-border">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Filter By */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Show</label>
                <select
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value as FilterOption)}
                  className="w-full bg-background border border-border rounded-md px-3 py-2 text-foreground focus:border-secret-primary focus:outline-none transition-colors"
                >
                  <option value="all">All Items ({items.length})</option>
                  <option value="exclusive">SECRET Exclusive ({exclusiveCount})</option>
                  <option value="available">Available Now ({availableCount})</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="w-full bg-background border border-border rounded-md px-3 py-2 text-foreground focus:border-secret-primary focus:outline-none transition-colors"
                >
                  <option value="exclusive_first">SECRET First</option>
                  <option value="name">Name A-Z</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                </select>
              </div>
            </div>

            {/* Quick Filter Chips */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterBy('exclusive')}
                className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm transition-all ${
                  filterBy === 'exclusive'
                    ? 'bg-secret-primary text-white'
                    : 'bg-muted text-muted-foreground hover:bg-secret-primary/20'
                }`}
              >
                <Shield size={12} />
                <span>SECRET Only</span>
              </button>
              <button
                onClick={() => setSortBy('price_low')}
                className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm transition-all ${
                  sortBy === 'price_low'
                    ? 'bg-secret-primary text-white'
                    : 'bg-muted text-muted-foreground hover:bg-secret-primary/20'
                }`}
              >
                <DollarSign size={12} />
                <span>Budget Friendly</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {filteredAndSortedItems.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-bold text-foreground mb-2">No items found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery
              ? `No menu items match "${searchQuery}"`
              : 'No items match your current filters'
            }
          </p>
          {(searchQuery || filterBy !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('')
                setFilterBy('all')
              }}
              className="secret-btn-secondary"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Main Menu Items - 4 Column Grid */}
          {(() => {
            const mainItems = filteredAndSortedItems.filter(item =>
              !item.name.toLowerCase().includes('honey') &&
              !item.name.toLowerCase().includes('sauce') &&
              !item.name.toLowerCase().includes('drink') &&
              !item.name.toLowerCase().includes('side')
            )
            const addOns = filteredAndSortedItems.filter(item =>
              item.name.toLowerCase().includes('honey') ||
              item.name.toLowerCase().includes('sauce') ||
              item.name.toLowerCase().includes('drink') ||
              item.name.toLowerCase().includes('side')
            )

            return (
              <>
                {mainItems.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                    {mainItems.map(item => (
                      <MenuItemCard
                        key={item.id}
                        item={item}
                        eventStatus={eventStatus}
                      />
                    ))}
                  </div>
                )}

                {/* Add-ons Section */}
                {addOns.length > 0 && (
                  <div className="mt-16">
                    <div className="mb-6">
                      <h3 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-2">Add-ons</h3>
                      <p className="text-muted-foreground text-sm">
                        Complete your meal with these delicious extras
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                      {addOns.map(item => (
                        <MenuItemCard
                          key={item.id}
                          item={item}
                          eventStatus={eventStatus}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )
          })()}
        </>
      )}

      {/* Legend */}
      {items.some(item => item.is_secret_exclusive) && (
        <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground pt-6 border-t border-border">
          <div className="flex items-center space-x-1">
            <Shield size={14} className="text-secret-primary" />
            <span>SECRET Exclusive - VISIBLE ONLY to SECRET members— like you!</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default MenuGrid